import { z } from "zod";
import { FEEDBACK_NOTICE_STATUSES, FEEDBACK_TYPES } from "./feedback.ts";
import { taskCommentBodySchema } from "./tasks.ts";
import { userIdSchema } from "./tenancy.ts";
import { wikiPassageAnchorSchema } from "./wiki-passage.ts";
import { WIKI_RESOLVE_ANSWERED_MAX, wikiThreadAgentStateSchema } from "./wiki-resolve.ts";

// Review threads on change sets and their items (wiki-collaboration D13–D16).

/** A comment's longest plain text, as task comments. */
export const WIKI_COMMENT_MAX = 2000;
export const wikiThreadKindSchema = z.enum(["comment", "question", "change_request"]);
export type WikiThreadKind = z.infer<typeof wikiThreadKindSchema>;
export const wikiThreadStatusSchema = z.enum(["open", "resolved"]);
export type WikiThreadStatus = z.infer<typeof wikiThreadStatusSchema>;
/** A comment the system posts in a thread: the agent revised the item (D15). */
export const wikiSystemCommentSchema = z.enum(["agent_revised"]);
/**
 * A comment's text, as task comments count it: NFC, trimmed, 1 to 2 000 code points (as the
 * composer's counter and Postgres' `char_length`), no control characters but line breaks.
 */
export const wikiCommentBodySchema = taskCommentBodySchema;

/** A decision note's longest text (wiki-collaboration-ux D16), in code points. */
export const WIKI_RESOLUTION_NOTE_MAX = 280;
/**
 * A decision note on Resolve (D16): one line ("accepted / rejected because …, affects …"), NFC,
 * trimmed, 1 to 280 code points (Postgres' `char_length`), no control characters, no line break.
 * Empty after trimming means no note.
 */
export const wikiResolutionNoteSchema = z
  .string()
  .max(4 * WIKI_RESOLUTION_NOTE_MAX)
  .transform((raw) => raw.normalize("NFC").trim())
  .refine((note) => [...note].length <= WIKI_RESOLUTION_NOTE_MAX && !/\p{Cc}/u.test(note), {
    message: `A decision note is one line of at most ${WIKI_RESOLUTION_NOTE_MAX} characters`,
  })
  .transform((note) => note || undefined);
/** Resolve's optional body (D16); an N-1 SPA sends none. */
export const resolveWikiThreadRequestSchema = z.object({
  note: wikiResolutionNoteSchema.optional(),
});
export type ResolveWikiThreadRequest = z.input<typeof resolveWikiThreadRequestSchema>;

/** `POST …/change-sets/:id/threads/resolve-answered` (D5). */
export const resolveAnsweredRequestSchema = z.object({
  threadIds: z.array(z.uuid()).min(1).max(WIKI_RESOLVE_ANSWERED_MAX),
  note: wikiResolutionNoteSchema.optional(),
});
export type ResolveAnsweredRequest = z.input<typeof resolveAnsweredRequestSchema>;
export const resolveAnsweredResultSchema = z.object({
  resolved: z.array(z.uuid()),
  skipped: z.array(z.uuid()),
});
export type ResolveAnsweredResult = z.infer<typeof resolveAnsweredResultSchema>;

export const openWikiThreadRequestSchema = z
  .object({
    /** The item it is about; absent: the change set itself. */
    itemId: z.uuid().optional(),
    kind: wikiThreadKindSchema,
    body: wikiCommentBodySchema,
    /** A change request on an item addressed to the agent (D15). */
    forAgent: z.boolean().optional(),
    /**
     * The passage of the item it is about (wiki-collaboration-ux D2), checked against the item
     * as the viewer reads it. Absent: the item or the set as a whole (an N-1 SPA sends none).
     */
    anchor: wikiPassageAnchorSchema.optional(),
  })
  .refine((r) => !r.forAgent || (r.kind === "change_request" && r.itemId !== undefined), {
    message: "Only a change request on an item can be addressed to the agent",
    path: ["forAgent"],
  })
  .refine((r) => !r.anchor || r.itemId !== undefined, {
    message: "Only a thread on an item can quote a passage",
    path: ["anchor"],
  });
export type OpenWikiThreadRequest = z.infer<typeof openWikiThreadRequestSchema>;
export const replyWikiThreadRequestSchema = z.object({ body: wikiCommentBodySchema });
export type ReplyWikiThreadRequest = z.infer<typeof replyWikiThreadRequestSchema>;

export const wikiCommentSchema = z.object({
  id: z.uuid(),
  /** Null for a system comment. */
  authorId: userIdSchema.nullable(),
  authorName: z.string().nullable(),
  system: wikiSystemCommentSchema.nullable(),
  /** Null once deleted (a tombstone) and for a system comment. */
  body: z.string().nullable(),
  deleted: z.boolean(),
  createdAt: z.string(),
  /** Whether the viewer may delete it: its author or a `wiki.delete_page` holder. */
  mayDelete: z.boolean(),
});
export type WikiComment = z.infer<typeof wikiCommentSchema>;

export const wikiThreadSchema = z.object({
  id: z.uuid(),
  changeSetId: z.uuid(),
  /** Null: a thread on the change set itself. */
  itemId: z.uuid().nullable(),
  kind: wikiThreadKindSchema,
  status: wikiThreadStatusSchema,
  createdBy: userIdSchema,
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  resolvedBy: userIdSchema.nullable(),
  resolvedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  /** Addressed to the agent (D15): asked, and revised by the agent once it rewrote the item. */
  forAgent: z.boolean(),
  agentRevisedAt: z.string().nullable(),
  /** Whether the viewer may resolve or reopen it (D14). */
  mayResolve: z.boolean(),
  /** Whether the viewer may reply (the set's threads are read-only once it is closed). */
  mayReply: z.boolean(),
  /** The quoted passage (wiki-collaboration-ux D2); null or absent: the item or set as a whole. */
  anchor: wikiPassageAnchorSchema.nullable().optional(),
  /** The resolver's decision note (D16), while resolved; null or absent: none (or an N-1 API). */
  resolutionNote: z.string().nullable().optional(),
  comments: z.array(wikiCommentSchema),
  /**
   * The agent's replies on it as the viewer reads them, and who a question waits on
   * (wiki-resolve-with-agent D6, D10). Absent: none, or an N-1 API.
   */
  agentReplies: wikiThreadAgentStateSchema.optional(),
});
export type WikiThread = z.infer<typeof wikiThreadSchema>;
export const wikiThreadListSchema = z.object({ threads: z.array(wikiThreadSchema) });
export type WikiThreadList = z.infer<typeof wikiThreadListSchema>;

/**
 * An open thread as a change set's view counts it (no text): the review's "Changes requested"
 * marks and the queue's rows (D16). Only threads whose anchor the viewer sees.
 */
export const wikiOpenThreadSchema = z.object({
  itemId: z.uuid().nullable(),
  kind: wikiThreadKindSchema,
  /** Who opened it: a thread never waits on its own author. Absent from an older API. */
  createdBy: userIdSchema.optional(),
  /** A change request the agent was asked to revise and has not revised yet. */
  forAgent: z.boolean().optional(),
});
export type WikiOpenThread = z.infer<typeof wikiOpenThreadSchema>;

// ── Trust levels (D9) ──────────────────────────────────────────────────────────────────────

/** `wiki.trust`: who may approve and publish a change set. */
export const wikiTrustLevelSchema = z.enum(["requester", "peer", "manager"]);
export type WikiTrustLevel = z.infer<typeof wikiTrustLevelSchema>;
/** Higher is stricter: a project only tightens its inherited level. */
export const WIKI_TRUST_STRICTNESS: Record<WikiTrustLevel, number> = {
  requester: 0,
  peer: 1,
  manager: 2,
};

// ── The Revision requested card (D15) ──────────────────────────────────────────────────────

/** An open change request for the agent, as the creator's conversation shows it. */
export const wikiRevisionRequestSchema = z.object({
  threadId: z.uuid(),
  changeSetId: z.uuid(),
  itemId: z.uuid(),
  itemTitle: z.string(),
  requestedBy: userIdSchema,
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  comment: z.string(),
  /** The passage the request is about (wiki-collaboration-ux 12.7); absent: the whole item. */
  quote: z.string().optional(),
});
export type WikiRevisionRequest = z.infer<typeof wikiRevisionRequestSchema>;
export const wikiRevisionRequestListSchema = z.object({
  requests: z.array(wikiRevisionRequestSchema),
});

/**
 * Stored with the server-composed user message of a Revise turn: the card's settled line after
 * the click. IDs and the item's title only; the comment is in the message text.
 */
export const REVISION_REQUEST_DATA_NAME = "wiki-revision";
export const REVISION_REQUEST_PART = `data-${REVISION_REQUEST_DATA_NAME}` as const;
export const revisionRequestPartSchema = z.object({
  type: z.literal(REVISION_REQUEST_PART),
  data: z.object({
    threadId: z.uuid(),
    changeSetId: z.uuid(),
    itemId: z.uuid(),
    itemTitle: z.string(),
    requestedByName: z.string().nullable(),
    /** The passage the request is about; the item's own text (wiki-collaboration-ux 12.7). */
    quote: z.string().max(500).optional(),
  }),
});
export type RevisionRequestPart = z.infer<typeof revisionRequestPartSchema>;

// ── Threads on published pages (D17) ───────────────────────────────────────────────────────

/** A page thread is a comment or a question: nothing on a published page waits on it. */
export const wikiPageThreadKindSchema = z.enum(["comment", "question"]);
export type WikiPageThreadKind = z.infer<typeof wikiPageThreadKindSchema>;

/** A heading of the page's current version, by its stable key (`headingAnchors`). */
export const wikiHeadingKeySchema = z.object({
  slug: z.string().min(1).max(200),
  occurrence: z.number().int().min(0).max(1000),
});
export type WikiHeadingKey = z.infer<typeof wikiHeadingKeySchema>;

export const openWikiPageThreadRequestSchema = z
  .object({
    /**
     * The heading it is about (an N-1 SPA's heading thread); absent: the page as a whole or a
     * passage. Checked against the current version.
     */
    heading: wikiHeadingKeySchema.optional(),
    /**
     * The passage it is about (wiki-collaboration-ux D2), checked against the page's current
     * version as the viewer reads it (`422 anchor_not_found` otherwise).
     */
    anchor: wikiPassageAnchorSchema.optional(),
    kind: wikiPageThreadKindSchema,
    body: wikiCommentBodySchema,
  })
  .refine((r) => !(r.heading && r.anchor), {
    message: "A thread is on a heading or a passage, not both",
    path: ["anchor"],
  });
export type OpenWikiPageThreadRequest = z.infer<typeof openWikiPageThreadRequestSchema>;

export const wikiPageThreadSchema = wikiThreadSchema
  .omit({ changeSetId: true, itemId: true, forAgent: true, agentRevisedAt: true })
  .extend({
    pageId: z.uuid(),
    /** Null: a thread on the page as a whole. */
    heading: wikiHeadingKeySchema.nullable(),
    /** The version it was opened on. */
    anchoredVersion: z.number().int().min(1),
    /** A later version dropped its heading: it reads "On an earlier version". */
    detached: z.boolean(),
    /** Whether the viewer may ask the agent about it (`wiki.draft`). */
    mayAsk: z.boolean(),
    /** The conversation the viewer started from it with "Ask the agent"; only theirs. */
    askedConversationId: z.uuid().nullable(),
  });
export type WikiPageThread = z.infer<typeof wikiPageThreadSchema>;
export const wikiPageThreadListSchema = z.object({ threads: z.array(wikiPageThreadSchema) });
export type WikiPageThreadList = z.infer<typeof wikiPageThreadListSchema>;

// ── In-app notifications (D18) ─────────────────────────────────────────────────────────────

export const WIKI_NOTIFICATIONS_LISTED = 50;
export const wikiNotificationKindSchema = z.enum([
  "change_set_submitted",
  "change_request",
  "question",
  "reply",
  "resolved",
  "revision_done",
  "page_thread",
  // Task notifications (tasks-notifications): listed only to clients sending `include=tasks`.
  "task_assigned",
  "task_due_soon",
  // A reporter's own report changed status (feedback-reporter-status): only with `feedback=1`.
  "feedback_status",
  // The author sent the agent's replies (wiki-resolve-with-agent D11): only with `agent=1`.
  "agent_replies_sent",
]);
/** The kinds an older client never lists (tasks-notifications, N-1). */
export const TASK_NOTIFICATION_KINDS = ["task_assigned", "task_due_soon"] as const;
/** The kinds a client lists only when it sends `agent=1` (wiki-resolve-with-agent, N-1). */
export const AGENT_NOTIFICATION_KINDS = ["agent_replies_sent"] as const;
export type WikiNotificationKind = z.infer<typeof wikiNotificationKindSchema>;
/** The kinds stored in `wiki_notifications` (a feedback entry lives in its own table). */
export type StoredNotificationKind = Exclude<WikiNotificationKind, "feedback_status">;

/**
 * A notification as its recipient reads it, rendered at read time: the change set's or page's
 * title only when they may read it (else null, shown as "a change set" / "a page"), the actor's
 * name, and where it leads. Never comment text.
 */
export const wikiNotificationSchema = z.object({
  id: z.uuid(),
  kind: wikiNotificationKindSchema,
  /** Null for a feedback entry only (no project), which an older client never receives. */
  projectId: z.uuid().nullable(),
  /** Null when the recipient may not read the project any more. */
  projectName: z.string().nullable(),
  /** What it is about: a change set, a page, a task or the caller's own feedback report. */
  subject: z.enum(["change_set", "page", "task", "feedback"]),
  /** The set's, page's or task's title, or null when the recipient may not read it. */
  title: z.string().nullable(),
  actorName: z.string().nullable(),
  /** Where opening it goes; null when the recipient may not open it. */
  changeSetId: z.uuid().nullable(),
  pageId: z.uuid().nullable(),
  threadId: z.uuid().nullable(),
  /** The task it opens (task notifications); null when the recipient may not open it. */
  taskId: z.uuid().nullable().optional(),
  /** The task's due date (`YYYY-MM-DD`), only alongside its title. */
  dueDate: z.string().nullable().optional(),
  /**
   * `agent_replies_sent` (wiki-resolve-with-agent D11): how many of the recipient's threads got a
   * reply in that send action, and how many of those are questions to them. Counts only.
   */
  agentReplies: z.object({ replies: z.int().min(1), questions: z.int().min(0) }).optional(),
  /**
   * Feedback entries (feedback-reporter-status): the caller's own report's type and the status
   * it reached, the release that fixed it when known, and the operator's reason for
   * `not_planned` while the group still is.
   */
  feedback: z
    .object({
      type: z.enum(FEEDBACK_TYPES),
      status: z.enum(FEEDBACK_NOTICE_STATUSES),
      fixedIn: z.string().optional(),
      reason: z.string().optional(),
    })
    .optional(),
  createdAt: z.string(),
  read: z.boolean(),
});
export type WikiNotification = z.infer<typeof wikiNotificationSchema>;
export const wikiNotificationListSchema = z.object({
  items: z.array(wikiNotificationSchema),
  unread: z.number().int().min(0),
});
export type WikiNotificationList = z.infer<typeof wikiNotificationListSchema>;
/** `include=tasks`: list task notifications too (tasks-notifications); older clients omit it. */
export const wikiNotificationListQuerySchema = z.object({
  include: z.literal("tasks").optional(),
  /**
   * `feedback=1`: the caller's feedback notifications too (feedback-reporter-status). A separate
   * key, not an `include` value: an older API ignores it rather than refusing the request.
   */
  feedback: z.literal("1").optional(),
  /** `agent=1`: `agent_replies_sent` entries too (wiki-resolve-with-agent); older clients omit it. */
  agent: z.literal("1").optional(),
});
export type WikiNotificationListQuery = z.infer<typeof wikiNotificationListQuerySchema>;
export const markWikiNotificationsReadRequestSchema = z.object({
  /** These notifications; absent: every one ("Mark all read"). */
  ids: z.array(z.uuid()).min(1).max(WIKI_NOTIFICATIONS_LISTED).optional(),
});
export type MarkWikiNotificationsReadRequest = z.infer<
  typeof markWikiNotificationsReadRequestSchema
>;

// ── "Ask the agent" from a page thread (D17) ───────────────────────────────────────────────

/**
 * Stored with the server-composed first message of an Ask turn: where it came from. IDs, the
 * page's title and the heading's key; the thread's words are in the message text.
 */
export const WIKI_ASK_DATA_NAME = "wiki-ask";
export const WIKI_ASK_PART = `data-${WIKI_ASK_DATA_NAME}` as const;
export const wikiAskPartSchema = z.object({
  type: z.literal(WIKI_ASK_PART),
  data: z.object({
    pageId: z.uuid(),
    threadId: z.uuid(),
    pageTitle: z.string(),
    heading: wikiHeadingKeySchema.nullable(),
  }),
});
export type WikiAskPart = z.infer<typeof wikiAskPartSchema>;
